import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getTemplates, createTemplate, deleteTemplate } from '../../api/templateManager'
import type { DocumentTemplate, StructureType, StructureConfig } from '../../api/templateManager'
import { parseDocumentAuto } from '../../api/documentParser'
import CustomerCombobox from '../../components/CustomerCombobox'
import { useCustomersByRole } from '../../hooks/useCustomersByRole'
import * as XLSX from 'xlsx'

// Z58.24: same 4 structure types the parser (AutoJobsPage) knows how to
// dispatch on. single_hbl is the default/only shape that existed before this.
const STRUCTURE_TYPE_OPTIONS: { value: StructureType; label: string; description: string }[] = [
  { value: 'single_hbl', label: 'Single HBL, one file', description: 'Most common — one file, one HBL' },
  { value: 'manifest', label: 'Manifest', description: 'Multiple HBLs listed sequentially in one file (tabular)' },
  { value: 'multi_sheet_excel', label: 'Multi-sheet Excel', description: 'Each sheet is a separate HBL' },
  { value: 'single_page_multi_hbl', label: 'Single page, multiple HBL blocks', description: 'Rare — e.g. consol lists' },
]

const EMPTY_STRUCTURE_FORM = {
  header_marker: '',
  block_separator: '',
  hbl_count_field: '',
  sheet_filter: '',
  skip_sheets: '',
  hbl_ref_cell: '',
  hbl_delimiter_pattern: '',
  hbl_ref_pattern: '',
}

type TemplateType = 'MAWB' | 'HAWB' | 'MBL' | 'HBL'
type TabType = 'Master' | 'House'

export default function DocumentTemplatesPage() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<TabType>('Master')
  const [templates, setTemplates] = useState<DocumentTemplate[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showTrainModal, setShowTrainModal] = useState(false)
  const [trainingState, setTrainingState] = useState<'type' | 'upload' | 'review' | 'confirm'>('type')
  const [selectedTemplateType, setSelectedTemplateType] = useState<TemplateType>('MBL')
  const [trainFile, setTrainFile] = useState<File | null>(null)
  const [parsedData, setParsedData] = useState<any>(null)
  const [isParsing, setIsParsing] = useState(false)
  const [templateForm, setTemplateForm] = useState({
    template_name: '',
    document_type: 'MBL' as 'MAWB' | 'HAWB' | 'MBL' | 'HBL',
    transport_mode: 'SEA' as 'AIR' | 'SEA' | 'ROAD',
    direction: 'BOTH' as 'IMP' | 'EXP' | 'BOTH',
    carrier_agent_name: '',
    vessel_flight: '',
    voyage_number: '',
    port_loading: '',
    port_discharge: '',
    detection_keywords: '',
    // Z58.24
    customer_id: '',
    structure_type: 'single_hbl' as StructureType,
    structure_form: EMPTY_STRUCTURE_FORM,
  })
  const [editingData, setEditingData] = useState<any>(null)
  // Z58.24: customer dropdown for the template form + resolving customer_name
  // to denormalize at save time (same pattern as invoice_templates); customer
  // filter for the templates list.
  const { customers } = useCustomersByRole('Cus')
  const [customerFilter, setCustomerFilter] = useState('')
  const [structureFilter, setStructureFilter] = useState<StructureType | ''>('')

  const templateTypes: { [key in TabType]: TemplateType[] } = {
    Master: ['MAWB', 'MBL'],
    House: ['HAWB', 'HBL']
  }

  useEffect(() => {
    loadTemplates()
  }, [activeTab])

  async function loadTemplates() {
    try {
      setLoading(true)
      const types = templateTypes[activeTab]
      const data = await getTemplates()
      setTemplates(data.filter(t => types.includes(t.template_type)))
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load templates')
    } finally {
      setLoading(false)
    }
  }

  function normalizeData(data: any) {
    if (!data) return {}
    return {
      ...data,
      shipper: data.shipper || { name: '', address: '' },
      consignee: data.consignee || { name: '', address: '', contact: '' },
      ports: data.ports || { loading: '', discharge: '', delivery: '' },
      cargo: data.cargo || { description: '', pieces: 0, gross_weight_kgs: 0, net_weight_kgs: 0, volume_cbm: 0 },
      container: data.container || { number: '', seal: '', type: '', packages: '' }
    }
  }

  function getCellValue(worksheet: XLSX.WorkSheet, cellRef: string): string {
    const cell = worksheet[cellRef]
    if (!cell) return ''
    return cell.v?.toString() || ''
  }

  function isMultiSheetHBLExcel(workbook: XLSX.WorkBook): boolean {
    if (workbook.SheetNames.length < 2) return false

    // Check if all sheet names look like HBL references (6-10 alphanumeric chars)
    const hblPattern = /^[A-Z0-9]{6,10}$/
    return workbook.SheetNames.every(name => hblPattern.test(name))
  }

  function parseMultiSheetHBLExcel(file: File): Promise<any> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const data = e.target?.result
          const workbook = XLSX.read(data, { type: 'array' })

          if (!isMultiSheetHBLExcel(workbook)) {
            reject(new Error('Not a multi-sheet HBL Excel format'))
            return
          }

          // Parse first sheet only
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]]
          console.log('[DocumentTemplatesPage] Parsing multi-sheet HBL Excel, sheet:', workbook.SheetNames[0])

          // Extract from fixed positions
          const blNumberFull = getCellValue(firstSheet, 'H1').trim() // rows[0][7]
          const blNumber = blNumberFull.split('：').pop()?.trim() || blNumberFull

          const shipper = getCellValue(firstSheet, 'B2').trim() // rows[1][1]
          const consignee = getCellValue(firstSheet, 'B6').trim() // rows[5][1]
          const vessel = getCellValue(firstSheet, 'B13').trim() // rows[12][1]
          const portLoading = getCellValue(firstSheet, 'C13').trim() // rows[12][2]
          const portDischarge = getCellValue(firstSheet, 'B15').trim() // rows[14][1]
          const packages = getCellValue(firstSheet, 'C17').trim() // rows[16][2]
          const cargoDescription = getCellValue(firstSheet, 'D17').trim() // rows[16][3]
          const containerNumber = getCellValue(firstSheet, 'B23').trim() // rows[22][1]

          // Extract carrier/agent name from BL area
          const carrierCell = getCellValue(firstSheet, 'H1').split('\n')[0] || ''
          const carrierName = carrierCell.split('：')[0]?.trim() || 'Unknown'

          console.log('[DocumentTemplatesPage] Extracted multi-sheet HBL data:', {
            blNumber,
            shipper,
            consignee,
            vessel,
            portLoading,
            portDischarge,
            packages,
            cargoDescription,
            containerNumber,
            carrierName
          })

          const result = {
            mbl_number: blNumber || undefined,
            shipper: { name: shipper || undefined },
            consignee: { name: consignee || undefined },
            vessel_name: vessel || undefined,
            ports: {
              loading: portLoading || undefined,
              discharge: portDischarge || undefined
            },
            container: {
              number: containerNumber || undefined,
              packages: packages || undefined
            },
            cargo: {
              description: cargoDescription || undefined
            },
            transport_mode: 'SEA',
            direction: 'IMP',
            carrier: carrierName
          }

          resolve(result)
        } catch (err) {
          reject(err instanceof Error ? err : new Error('Failed to parse multi-sheet HBL Excel'))
        }
      }
      reader.onerror = () => reject(new Error('Failed to read file'))
      reader.readAsArrayBuffer(file)
    })
  }

  async function handleTrainSubmit(file: File) {
    try {
      setError('')
      setIsParsing(true)

      // Try 发票箱单 format first (check if A1 cell = '海运分单')
      if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          file.type === 'application/vnd.ms-excel' ||
          file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        try {
          const invoiceBoxData = await new Promise<any>((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = (e) => {
              try {
                const data = e.target?.result
                const workbook = XLSX.read(data, { type: 'array' })
                const worksheet = workbook.Sheets[workbook.SheetNames[0]]

                // Check if this is 发票箱单 format (A1 = '海运分单')
                const a1Value = getCellValue(worksheet, 'A1').trim()
                if (a1Value === '海运分单') {
                  console.log('[DocumentTemplatesPage] Detected 发票箱单 format')

                  // Parse using fixed positions
                  const hblNumber = getCellValue(worksheet, 'D2').trim() // rows[1][3]
                  const clientRef = getCellValue(worksheet, 'B2').trim() // rows[1][1]
                  const shipper = getCellValue(worksheet, 'B14').trim() // rows[13][1]
                  const consignee = getCellValue(worksheet, 'B19').trim() // rows[18][1]
                  const eori = getCellValue(worksheet, 'B24').trim() // rows[23][1]
                  const vat = getCellValue(worksheet, 'B23').trim() // rows[22][1]
                  const invoiceNo = getCellValue(worksheet, 'B28').trim() // rows[27][1]

                  // Extract line items from row 29+
                  const lineItems = []
                  let rowIdx = 29
                  const maxRows = 100

                  while (rowIdx < maxRows) {
                    const hsCodeCell = XLSX.utils.encode_col(0) + XLSX.utils.encode_row(rowIdx)
                    const hsCode = getCellValue(worksheet, hsCodeCell).trim()

                    if (!hsCode || isNaN(Number(hsCode))) break

                    const descCell = XLSX.utils.encode_col(1) + XLSX.utils.encode_row(rowIdx)
                    const description = getCellValue(worksheet, descCell).trim()

                    lineItems.push({
                      hs_code: hsCode,
                      goods_description: description
                    })

                    rowIdx++
                  }

                  const result = {
                    mbl_number: hblNumber || undefined,
                    waybill_number: invoiceNo || undefined,
                    shipper: { name: shipper || undefined },
                    consignee: { name: consignee || undefined, contact: eori || undefined },
                    container: { packages: lineItems.length.toString() },
                    cargo: {
                      description: lineItems.map(item => `${item.hs_code}: ${item.goods_description}`).join('; ') || undefined,
                      pieces: lineItems.length
                    },
                    transport_mode: 'SEA',
                    direction: 'IMP',
                    carrier: '发票箱单',
                    vat_number: vat || undefined,
                    summary: `Invoice #${invoiceNo} - ${lineItems.length} items, Ref: ${clientRef || 'N/A'}`
                  }

                  resolve(result)
                } else {
                  reject(new Error('Not 发票箱单 format'))
                }
              } catch (err) {
                reject(err)
              }
            }
            reader.onerror = () => reject(new Error('Failed to read file'))
            reader.readAsArrayBuffer(file)
          })

          const normalized = normalizeData(invoiceBoxData)
          console.log('[DocumentTemplatesPage] Detected 发票箱单 Excel, parsed data:', normalized)
          setParsedData(normalized)
          setEditingData(null)
          setSelectedTemplateType('HBL')
          setIsParsing(false)
          setTrainingState('review')
          return
        } catch (err) {
          console.log('[DocumentTemplatesPage] Not 发票箱单 format, trying multi-sheet HBL Excel')
        }

        // Try multi-sheet HBL Excel detection
        try {
          const hblData = await parseMultiSheetHBLExcel(file)
          const normalized = normalizeData(hblData)
          console.log('[DocumentTemplatesPage] Detected multi-sheet HBL Excel, parsed data:', normalized)
          setParsedData(normalized)
          setEditingData(null)
          setSelectedTemplateType('HBL')
          setIsParsing(false)
          setTrainingState('review')
          return
        } catch (err) {
          console.log('[DocumentTemplatesPage] Not a multi-sheet HBL Excel, falling back to standard parsing')
        }
      }

      // Try template detection for PDF files before standard parsing
      if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
        console.log('[DocumentTemplatesPage] Attempting template detection for PDF')
        try {
          const reader = new FileReader()
          reader.onload = async () => {
            try {
              const dataUrl = reader.result as string
              const base64Parts = dataUrl.split(',')

              if (base64Parts.length !== 2) {
                throw new Error('Invalid file encoding')
              }

              const base64 = base64Parts[1]

              // Try template detection with Edge Function
              const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
              const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

              if (!supabaseUrl || !anonKey) {
                throw new Error('Missing Supabase configuration')
              }

              const response = await fetch(`${supabaseUrl}/functions/v1/parse-document`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${anonKey}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  fileContent: base64,
                  mimeType: file.type,
                  scenario: 'template_detect'
                })
              })

              const templateDetectionResult = await response.json()

              if (response.ok && templateDetectionResult.template_type === 'HBL' && templateDetectionResult.format === 'multi_hbl_pdf') {
                console.log('[DocumentTemplatesPage] Template detection successful:', templateDetectionResult)

                // Merge sample data with normalized structure
                const mergedData = {
                  ...normalizeData(templateDetectionResult.sample_data),
                  transport_mode: templateDetectionResult.transport_mode || 'SEA',
                  direction: templateDetectionResult.direction || 'IMP'
                }

                setParsedData(mergedData)
                setEditingData(null)
                setSelectedTemplateType('HBL')
                setIsParsing(false)
                setTrainingState('review')
                return
              } else {
                console.log('[DocumentTemplatesPage] Template detection not applicable, falling back to standard parsing')
              }
            } catch (err) {
              console.log('[DocumentTemplatesPage] Template detection failed, falling back to standard parsing:', err)
            }

            // Fall back to standard parsing if template detection fails
            try {
              const dataUrl = reader.result as string
              const base64Parts = dataUrl.split(',')

              if (base64Parts.length !== 2) {
                throw new Error('Invalid file encoding')
              }

              const base64 = base64Parts[1]
              const result = await parseDocumentAuto(base64, file.type, 'single')

              if (!result) {
                throw new Error('Failed to parse document')
              }

              const normalized = normalizeData(result)
              console.log('[DocumentTemplatesPage] Parsed data:', normalized)
              setParsedData(normalized)
              setEditingData(null)
              setIsParsing(false)
              setTrainingState('review')
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Failed to parse document')
              setIsParsing(false)
            }
          }
          reader.readAsDataURL(file)
          return
        } catch (err) {
          console.log('[DocumentTemplatesPage] PDF template detection error, will try standard parsing:', err)
        }
      }

      // Fall back to standard parsing with AI for non-PDF or failed template detection
      const reader = new FileReader()

      reader.onload = async () => {
        try {
          const dataUrl = reader.result as string
          const base64Parts = dataUrl.split(',')

          if (base64Parts.length !== 2) {
            throw new Error('Invalid file encoding')
          }

          const base64 = base64Parts[1]
          const result = await parseDocumentAuto(base64, file.type, 'single')

          if (!result) {
            throw new Error('Failed to parse document')
          }

          const normalized = normalizeData(result)
          console.log('[DocumentTemplatesPage] Parsed data:', normalized)
          setParsedData(normalized)
          setEditingData(null)
          setIsParsing(false)
          setTrainingState('review')
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to parse document')
          setIsParsing(false)
        }
      }

      reader.readAsDataURL(file)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error reading file')
      setIsParsing(false)
    }
  }

  async function handleSaveTemplate() {
    try {
      if (!templateForm.carrier_agent_name.trim()) {
        setError('Please enter carrier/agent name')
        return
      }

      if (!templateForm.detection_keywords.trim()) {
        setError('Please enter detection keywords')
        return
      }

      const keywords = templateForm.detection_keywords
        .split(',')
        .map(k => k.trim())
        .filter(k => k.length > 0)

      if (keywords.length === 0) {
        setError('Please enter at least one detection keyword')
        return
      }

      const fieldHints = {
        ...(parsedData || {}),
        template_name: templateForm.template_name,
        document_type: templateForm.document_type,
        transport_mode: templateForm.transport_mode,
        direction: templateForm.direction,
        vessel_flight: templateForm.vessel_flight,
        voyage_number: templateForm.voyage_number,
        ports: {
          loading: templateForm.port_loading,
          discharge: templateForm.port_discharge
        }
      }

      // Z58.24: only the fields relevant to the chosen structure_type are ever
      // written — an empty string just means "no hint, use the parser's default
      // detection for this type" (same as every field in structure_config being
      // optional in the interface).
      const sf = templateForm.structure_form
      const structureConfig: StructureConfig = {}
      if (templateForm.structure_type === 'manifest') {
        if (sf.header_marker.trim()) structureConfig.header_marker = sf.header_marker.trim()
        if (sf.block_separator.trim()) structureConfig.block_separator = sf.block_separator.trim()
        if (sf.hbl_count_field.trim()) structureConfig.hbl_count_field = sf.hbl_count_field.trim()
      } else if (templateForm.structure_type === 'multi_sheet_excel') {
        if (sf.sheet_filter.trim()) structureConfig.sheet_filter = sf.sheet_filter.trim()
        const skipSheets = sf.skip_sheets.split(',').map(s => s.trim()).filter(Boolean)
        if (skipSheets.length > 0) structureConfig.skip_sheets = skipSheets
        if (sf.hbl_ref_cell.trim()) structureConfig.hbl_ref_cell = sf.hbl_ref_cell.trim()
      } else if (templateForm.structure_type === 'single_page_multi_hbl') {
        if (sf.hbl_delimiter_pattern.trim()) structureConfig.hbl_delimiter_pattern = sf.hbl_delimiter_pattern.trim()
        if (sf.hbl_ref_pattern.trim()) structureConfig.hbl_ref_pattern = sf.hbl_ref_pattern.trim()
      }

      const customerName = templateForm.customer_id
        ? customers.find(c => c.id === templateForm.customer_id)?.name || null
        : null

      const template: DocumentTemplate = {
        template_type: templateForm.document_type,
        carrier_agent_name: templateForm.carrier_agent_name,
        detection_keywords: keywords,
        transport_mode: templateForm.transport_mode,
        direction: templateForm.direction,
        field_hints: JSON.stringify(fieldHints, null, 2),
        customer_id: templateForm.customer_id || null,
        customer_name: customerName,
        structure_type: templateForm.structure_type,
        structure_config: structureConfig,
      }

      await createTemplate(template)
      setError('')
      setShowTrainModal(false)
      setTrainingState('type')
      setSelectedTemplateType('MBL')
      setTrainFile(null)
      setParsedData(null)
      setTemplateForm({
        template_name: '',
        document_type: 'MBL',
        transport_mode: 'SEA',
        direction: 'BOTH',
        carrier_agent_name: '',
        vessel_flight: '',
        voyage_number: '',
        port_loading: '',
        port_discharge: '',
        detection_keywords: '',
        customer_id: '',
        structure_type: 'single_hbl',
        structure_form: EMPTY_STRUCTURE_FORM,
      })
      await loadTemplates()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template')
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Are you sure you want to delete this template?')) return

    try {
      await deleteTemplate(id)
      await loadTemplates()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete template')
    }
  }

  function suggestKeywords() {
    if (!parsedData) return

    const suggestions: string[] = []
    if (parsedData.carrier) suggestions.push(parsedData.carrier)
    if (parsedData.mbl_number) suggestions.push(parsedData.mbl_number.substring(0, 3))
    if (selectedTemplateType === 'MAWB' && parsedData.waybill_number) {
      suggestions.push(parsedData.waybill_number.substring(0, 3))
    }

    if (suggestions.length > 0) {
      setTemplateForm(prev => ({
        ...prev,
        detection_keywords: suggestions.join(', ')
      }))
    }
  }

  function preFillTemplateForm() {
    const carrier = editingData?.carrier || parsedData?.carrier || ''
    const parsedTransportMode = editingData?.transport_mode || parsedData?.transport_mode
    const transportMode = parsedTransportMode || (selectedTemplateType === 'MAWB' || selectedTemplateType === 'HAWB' ? 'AIR' : 'SEA')
    const direction = editingData?.direction || parsedData?.direction || 'BOTH'

    const keywords = carrier ? [carrier] : []

    setTemplateForm({
      template_name: carrier,
      document_type: selectedTemplateType,
      transport_mode: transportMode as 'AIR' | 'SEA' | 'ROAD',
      direction: direction as 'IMP' | 'EXP' | 'BOTH',
      carrier_agent_name: carrier,
      vessel_flight: editingData?.vessel_name || parsedData?.vessel_name || '',
      voyage_number: '',
      port_loading: editingData?.ports?.loading || parsedData?.ports?.loading || '',
      port_discharge: editingData?.ports?.discharge || parsedData?.ports?.discharge || '',
      detection_keywords: keywords.join(', '),
      customer_id: '',
      structure_type: 'single_hbl',
      structure_form: EMPTY_STRUCTURE_FORM,
    })
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/jobs')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 mb-4"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Jobs
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Document Templates</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Manage templates for automatic document detection</p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Tabs & Actions */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex gap-4 border-b border-gray-200 dark:border-gray-700">
              {(['Master', 'House'] as TabType[]).map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                    activeTab === tab
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  {tab} Documents
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowTrainModal(true)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
            >
              + Train from Document
            </button>
          </div>

          {/* Z58.24: filter by customer / structure type */}
          <div className="flex gap-3 mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
            {/* Z88: searchable (server-side) instead of a native <select> listing every customer */}
            <div className="flex items-center gap-1 w-64">
              <div className="flex-1"><CustomerCombobox value={customerFilter} onChange={setCustomerFilter} placeholder="All customers" /></div>
              {customerFilter && <button type="button" onClick={() => setCustomerFilter('')} className="text-xs text-gray-500 hover:text-gray-800 px-1" title="Show all customers">✕</button>}
            </div>
            <select
              value={structureFilter}
              onChange={e => setStructureFilter(e.target.value as StructureType | '')}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">All structure types</option>
              {STRUCTURE_TYPE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Templates List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading templates...</p>
          </div>
        ) : templates.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
            <p className="text-gray-600 dark:text-gray-400 mb-4">No templates yet</p>
            <button
              onClick={() => setShowTrainModal(true)}
              className="inline-block px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
            >
              Create First Template
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates
              .filter(t => !customerFilter || t.customer_id === customerFilter)
              .filter(t => !structureFilter || t.structure_type === structureFilter)
              .map(template => (
              <div
                key={template.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow"
              >
                {/* Header with badges */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      template.template_type.includes('M')
                        ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300'
                        : 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                    }`}>
                      {template.template_type}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      template.transport_mode === 'AIR'
                        ? 'bg-sky-100 dark:bg-sky-900/30 text-sky-800 dark:text-sky-300'
                        : template.transport_mode === 'SEA'
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                        : 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300'
                    }`}>
                      {template.transport_mode}
                    </span>
                    {!template.customer_id && (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300" title="No customer assigned yet — created before Z58.24">
                        ⚠️ Legacy template
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => handleDelete(template.id!)}
                    className="text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>

                {/* Carrier Name */}
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {template.carrier_agent_name}
                </h3>

                {/* Info Grid */}
                <div className="space-y-3 mb-4">
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">Customer</p>
                    <p className="text-sm text-gray-900 dark:text-white">{template.customer_name || '— none —'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">Structure Type</p>
                    <p className="text-sm text-gray-900 dark:text-white">
                      {STRUCTURE_TYPE_OPTIONS.find(o => o.value === template.structure_type)?.label || 'Single HBL, one file'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">Direction</p>
                    <p className="text-sm text-gray-900 dark:text-white">{template.direction}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">Detection Keywords</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {template.detection_keywords.slice(0, 3).map((keyword, i) => (
                        <span key={i} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded">
                          {keyword}
                        </span>
                      ))}
                      {template.detection_keywords.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded">
                          +{template.detection_keywords.length - 3}
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">Used</p>
                    <p className="text-sm text-gray-900 dark:text-white">{template.usage_count || 0} times</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Train Modal */}
        {showTrainModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Train Template from Document</h2>
              </div>

              <div className="p-6 space-y-6">
                {trainingState === 'type' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Template Type
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        {(['MAWB', 'HAWB', 'MBL', 'HBL'] as TemplateType[]).map(type => (
                          <button
                            key={type}
                            onClick={() => setSelectedTemplateType(type)}
                            className={`p-3 rounded-lg border-2 transition-all ${
                              selectedTemplateType === type
                                ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                                : 'border-gray-300 dark:border-gray-600'
                            }`}
                          >
                            <p className="font-semibold text-gray-900 dark:text-white">{type}</p>
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              {type.includes('M') ? 'Master' : 'House'}
                            </p>
                          </button>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={() => setTrainingState('upload')}
                      className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg"
                    >
                      Next
                    </button>
                  </>
                )}

                {trainingState === 'upload' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Upload Sample Document
                      </label>
                      <input
                        type="file"
                        onChange={e => setTrainFile(e.target.files?.[0] || null)}
                        accept=".pdf,.xlsx,.xls,.png,.jpg,.jpeg"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg"
                      />
                      {trainFile && <p className="mt-2 text-sm text-green-600">{trainFile.name}</p>}
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setTrainingState('type')}
                        disabled={isParsing}
                        className={`flex-1 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition-all ${
                          isParsing
                            ? 'opacity-50 cursor-not-allowed'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        Back
                      </button>
                      <button
                        onClick={() => trainFile && !isParsing && handleTrainSubmit(trainFile)}
                        disabled={!trainFile && !isParsing}
                        className={`flex-1 py-2 font-medium rounded-lg flex items-center justify-center gap-2 transition-all ${
                          isParsing
                            ? 'bg-blue-600 hover:bg-blue-600 text-white opacity-85 cursor-wait'
                            : 'bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-400 disabled:cursor-not-allowed'
                        }`}
                      >
                        {isParsing ? (
                          <>
                            <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                            <span>Parsing...</span>
                          </>
                        ) : (
                          'Parse Document'
                        )}
                      </button>
                    </div>
                    {isParsing && (
                      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 animate-pulse">
                        <p className="text-sm text-blue-700 dark:text-blue-300 flex items-center gap-2">
                          <span className="inline-block animate-spin text-base">🔄</span>
                          AI is analysing your document... (15-20 seconds)
                        </p>
                      </div>
                    )}
                  </>
                )}

                {trainingState === 'review' && parsedData && (
                  <>
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg mb-4">
                      <p className="text-sm text-blue-800 dark:text-blue-300">
                        ✅ Document parsed successfully. Please review and edit the extracted data below.
                      </p>
                    </div>

                    <div className="space-y-4 max-h-96 overflow-y-auto">
                      {/* Document Info */}
                      <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-3">Document Information</h4>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Transport Mode</label>
                            <input
                              type="text"
                              value={editingData?.transport_mode || parsedData?.transport_mode || ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingData((prev: any) => ({ ...(prev || {}), transport_mode: e.target.value }))}
                              className="w-full mt-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Direction</label>
                            <input
                              type="text"
                              value={editingData?.direction || parsedData?.direction || ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingData((prev: any) => ({ ...(prev || {}), direction: e.target.value }))}
                              className="w-full mt-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">MBL Number</label>
                            <input
                              type="text"
                              value={editingData?.mbl_number || parsedData?.mbl_number || ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingData((prev: any) => ({ ...(prev || {}), mbl_number: e.target.value }))}
                              className="w-full mt-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Waybill Number</label>
                            <input
                              type="text"
                              value={editingData?.waybill_number || parsedData?.waybill_number || ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingData((prev: any) => ({ ...(prev || {}), waybill_number: e.target.value }))}
                              className="w-full mt-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Carrier</label>
                            <input
                              type="text"
                              value={editingData?.carrier || parsedData?.carrier || ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingData((prev: any) => ({ ...(prev || {}), carrier: e.target.value }))}
                              className="w-full mt-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Vessel/Flight</label>
                            <input
                              type="text"
                              value={editingData?.vessel_name || parsedData?.vessel_name || ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingData((prev: any) => ({ ...(prev || {}), vessel_name: e.target.value }))}
                              className="w-full mt-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Parties */}
                      <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-3">Parties</h4>
                        <div className="grid grid-cols-1 gap-3">
                          <div>
                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Shipper</label>
                            <input
                              type="text"
                              value={editingData?.shipper?.name || parsedData?.shipper?.name || ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingData((prev: any) => ({
                                ...(prev || {}),
                                shipper: { ...(prev?.shipper || {}), name: e.target.value }
                              }))}
                              className="w-full mt-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Consignee</label>
                            <input
                              type="text"
                              value={editingData?.consignee?.name || parsedData?.consignee?.name || ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingData((prev: any) => ({
                                ...(prev || {}),
                                consignee: { ...(prev?.consignee || {}), name: e.target.value }
                              }))}
                              className="w-full mt-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Ports & Cargo */}
                      <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-3">Ports & Cargo</h4>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Port of Loading</label>
                            <input
                              type="text"
                              value={editingData?.ports?.loading || parsedData?.ports?.loading || ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingData((prev: any) => ({
                                ...(prev || {}),
                                ports: { ...(prev?.ports || {}), loading: e.target.value }
                              }))}
                              className="w-full mt-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Port of Discharge</label>
                            <input
                              type="text"
                              value={editingData?.ports?.discharge || parsedData?.ports?.discharge || ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingData((prev: any) => ({
                                ...(prev || {}),
                                ports: { ...(prev?.ports || {}), discharge: e.target.value }
                              }))}
                              className="w-full mt-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Cargo Description</label>
                            <input
                              type="text"
                              value={editingData?.cargo?.description || parsedData?.cargo?.description || ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingData((prev: any) => ({
                                ...(prev || {}),
                                cargo: { ...(prev?.cargo || {}), description: e.target.value }
                              }))}
                              className="w-full mt-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3 mt-4">
                      <button
                        onClick={() => setTrainingState('upload')}
                        className="flex-1 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        Back
                      </button>
                      <button
                        onClick={() => {
                          const finalData = editingData || parsedData
                          setParsedData(finalData)
                          preFillTemplateForm()
                          setTrainingState('confirm')
                        }}
                        className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg"
                      >
                        Continue
                      </button>
                    </div>
                  </>
                )}

                {trainingState === 'confirm' && (
                  <>
                    <div className="space-y-4 max-h-96 overflow-y-auto">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Template Name
                          </label>
                          <input
                            type="text"
                            value={templateForm.template_name}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTemplateForm(prev => ({ ...prev, template_name: e.target.value }))}
                            placeholder="e.g., China Eastern"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Document Type
                          </label>
                          <select
                            value={templateForm.document_type}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTemplateForm(prev => ({ ...prev, document_type: e.target.value as 'MAWB' | 'HAWB' | 'MBL' | 'HBL' }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            <option value="MAWB">MAWB (Master Waybill)</option>
                            <option value="HAWB">HAWB (House Waybill)</option>
                            <option value="MBL">MBL (Master B/L)</option>
                            <option value="HBL">HBL (House B/L)</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Transport Mode
                          </label>
                          <select
                            value={templateForm.transport_mode}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTemplateForm(prev => ({ ...prev, transport_mode: e.target.value as 'AIR' | 'SEA' | 'ROAD' }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            <option value="AIR">AIR</option>
                            <option value="SEA">SEA</option>
                            <option value="ROAD">ROAD</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Direction
                          </label>
                          <select
                            value={templateForm.direction}
                            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTemplateForm(prev => ({ ...prev, direction: e.target.value as 'IMP' | 'EXP' | 'BOTH' }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            <option value="BOTH">Both (IMP & EXP)</option>
                            <option value="IMP">Import Only</option>
                            <option value="EXP">Export Only</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Carrier/Agent Name
                        </label>
                        <input
                          type="text"
                          value={templateForm.carrier_agent_name}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTemplateForm(prev => ({ ...prev, carrier_agent_name: e.target.value }))}
                          placeholder="e.g., China Eastern, DHL Global Forwarding"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>

                      {/* Z58.24: customer identity — lets this template be matched by which
                          customer's docs it describes, not just layout/keywords. Optional —
                          leave blank for a generic/carrier-only template. */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Customer <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <CustomerCombobox
                          value={templateForm.customer_id}
                          onChange={id => setTemplateForm(prev => ({ ...prev, customer_id: id }))}
                          placeholder="— No specific customer —"
                        />
                      </div>

                      {/* Z58.24: which of the 4 known HBL file shapes this template
                          describes — the parser tries the matching detector first once a
                          template with this structure_type is matched. */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Structure Type
                        </label>
                        <div className="space-y-2">
                          {STRUCTURE_TYPE_OPTIONS.map(opt => (
                            <label
                              key={opt.value}
                              className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                                templateForm.structure_type === opt.value
                                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                  : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                              }`}
                            >
                              <input
                                type="radio"
                                name="structure_type"
                                checked={templateForm.structure_type === opt.value}
                                onChange={() => setTemplateForm(prev => ({ ...prev, structure_type: opt.value }))}
                                className="mt-1"
                              />
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">{opt.label}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{opt.description}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>

                      {templateForm.structure_type === 'manifest' && (
                        <div className="grid grid-cols-2 gap-4 p-3 bg-gray-50 dark:bg-gray-900/30 rounded-lg">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Header marker</label>
                            <input type="text" value={templateForm.structure_form.header_marker}
                              onChange={e => setTemplateForm(prev => ({ ...prev, structure_form: { ...prev.structure_form, header_marker: e.target.value } }))}
                              placeholder="e.g., HBL No" className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Block separator</label>
                            <input type="text" value={templateForm.structure_form.block_separator}
                              onChange={e => setTemplateForm(prev => ({ ...prev, structure_form: { ...prev.structure_form, block_separator: e.target.value } }))}
                              placeholder="e.g., ---" className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                          </div>
                          <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">HBL count field <span className="text-gray-400">(optional)</span></label>
                            <input type="text" value={templateForm.structure_form.hbl_count_field}
                              onChange={e => setTemplateForm(prev => ({ ...prev, structure_form: { ...prev.structure_form, hbl_count_field: e.target.value } }))}
                              placeholder="e.g., Total HBLs" className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                          </div>
                        </div>
                      )}

                      {templateForm.structure_type === 'multi_sheet_excel' && (
                        <div className="grid grid-cols-2 gap-4 p-3 bg-gray-50 dark:bg-gray-900/30 rounded-lg">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Sheet filter (regex)</label>
                            <input type="text" value={templateForm.structure_form.sheet_filter}
                              onChange={e => setTemplateForm(prev => ({ ...prev, structure_form: { ...prev.structure_form, sheet_filter: e.target.value } }))}
                              placeholder="e.g., ^HBL" className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">HBL ref cell</label>
                            <input type="text" value={templateForm.structure_form.hbl_ref_cell}
                              onChange={e => setTemplateForm(prev => ({ ...prev, structure_form: { ...prev.structure_form, hbl_ref_cell: e.target.value } }))}
                              placeholder="e.g., B2" className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                          </div>
                          <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Skip sheets (comma-separated)</label>
                            <input type="text" value={templateForm.structure_form.skip_sheets}
                              onChange={e => setTemplateForm(prev => ({ ...prev, structure_form: { ...prev.structure_form, skip_sheets: e.target.value } }))}
                              placeholder="e.g., Summary, 申报要素" className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                          </div>
                        </div>
                      )}

                      {templateForm.structure_type === 'single_page_multi_hbl' && (
                        <div className="grid grid-cols-1 gap-4 p-3 bg-gray-50 dark:bg-gray-900/30 rounded-lg">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">HBL delimiter pattern (regex)</label>
                            <input type="text" value={templateForm.structure_form.hbl_delimiter_pattern}
                              onChange={e => setTemplateForm(prev => ({ ...prev, structure_form: { ...prev.structure_form, hbl_delimiter_pattern: e.target.value } }))}
                              placeholder="e.g., ^HOUSE BILL OF LADING" className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">HBL ref pattern (regex)</label>
                            <input type="text" value={templateForm.structure_form.hbl_ref_pattern}
                              onChange={e => setTemplateForm(prev => ({ ...prev, structure_form: { ...prev.structure_form, hbl_ref_pattern: e.target.value } }))}
                              placeholder="e.g., HBL\\s*No\\.?\\s*:?\\s*(\\S+)" className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Vessel/Flight
                          </label>
                          <input
                            type="text"
                            value={templateForm.vessel_flight}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTemplateForm(prev => ({ ...prev, vessel_flight: e.target.value }))}
                            placeholder="e.g., MU701, NORTHERN STAR"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Voyage Number
                          </label>
                          <input
                            type="text"
                            value={templateForm.voyage_number}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTemplateForm(prev => ({ ...prev, voyage_number: e.target.value }))}
                            placeholder="e.g., 0051W"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Port of Loading
                          </label>
                          <input
                            type="text"
                            value={templateForm.port_loading}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTemplateForm(prev => ({ ...prev, port_loading: e.target.value }))}
                            placeholder="e.g., CNSHA"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Port of Discharge
                          </label>
                          <input
                            type="text"
                            value={templateForm.port_discharge}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTemplateForm(prev => ({ ...prev, port_discharge: e.target.value }))}
                            placeholder="e.g., GBFXT"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Detection Keywords (comma-separated)
                          </label>
                          <button
                            onClick={suggestKeywords}
                            className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400"
                          >
                            Auto-suggest
                          </button>
                        </div>
                        <textarea
                          value={templateForm.detection_keywords}
                          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setTemplateForm(prev => ({ ...prev, detection_keywords: e.target.value }))}
                          placeholder="e.g., China Eastern, MU, Air Waybill"
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>

                    <div className="flex gap-3 mt-4">
                      <button
                        onClick={() => setTrainingState('review')}
                        className="flex-1 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        Back
                      </button>
                      <button
                        onClick={handleSaveTemplate}
                        className="flex-1 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg"
                      >
                        Save Template
                      </button>
                    </div>
                  </>
                )}
              </div>

              <div className="p-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => {
                    setShowTrainModal(false)
                    setTrainingState('type')
                    setTrainFile(null)
                    setParsedData(null)
                    setTemplateForm({
                      template_name: '',
                      document_type: 'MBL',
                      transport_mode: 'SEA',
                      direction: 'BOTH',
                      carrier_agent_name: '',
                      vessel_flight: '',
                      voyage_number: '',
                      port_loading: '',
                      port_discharge: '',
                      detection_keywords: '',
                      customer_id: '',
                      structure_type: 'single_hbl',
                      structure_form: EMPTY_STRUCTURE_FORM,
                    })
                  }}
                  className="w-full px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
